import * as amplitude from '@amplitude/analytics-browser';

import {
  createAmplitudePrivacyBeforePlugin,
  createAmplitudePrivacyEnrichmentPlugin,
  createAmplitudePrivacyReadyLatch,
  getAmplitudeElementInteractionsOptions,
} from 'analytics/amplitudePrivacy';
import type {ConsentPreferences} from 'analytics/Destinations';
import {isSessionAnalyticsAllowed} from 'analytics/sessionTracking';
import BenchlingConfig from 'BenchlingConfig';

export type AmplitudeIdentityStorage = 'cookie' | 'none';

/** Spread into amplitude.init for identity persistence. */
export interface AmplitudeIdentityInitOptions {
  identityStorage: AmplitudeIdentityStorage;
  cookieOptions?: {domain: string};
}

/** Must match DestinationId.AMPLITUDE / ConsentState values in Destinations.ts */
const AMPLITUDE_DESTINATION_ID = 'Amplitude';
const EXPLICIT_AGREE = 'EXPLICIT_AGREE';
const EXPLICIT_DISAGREE = 'EXPLICIT_DISAGREE';

/**
 * Cookie names in a document.cookie header that look like Amplitude identity cookies.
 */
export function listAmplitudeIdentityCookieNames(cookieHeader: string): Array<string> {
  const names: Array<string> = [];
  for (const part of cookieHeader.split(';')) {
    const name = part.trim().split('=')[0];
    if (name.startsWith('AMP_')) {
      names.push(name);
    }
  }
  return names;
}

export interface ClearAmplitudeIdentityCookiesDeps {
  cookieHeader?: string;
  cookieDomain?: string;
  expireCookie?: (cookieAssignment: string) => void;
}

/**
 * Expire Amplitude identity cookies for the current hostname.
 *
 * Amplitude documents programmatic removal via document.cookie for AMP_* / AMP_MKTG_* cookies.
 * We only use identityStorage 'cookie' | 'none' (not localStorage).
 */
export function clearAmplitudeIdentityCookies(deps: ClearAmplitudeIdentityCookiesDeps = {}): void {
  const cookieHeader = deps.cookieHeader ?? document.cookie;
  const cookieDomain = deps.cookieDomain ?? window.location.hostname;
  const expireCookie =
    deps.expireCookie ??
    ((assignment: string) => {
      document.cookie = assignment;
    });

  for (const name of listAmplitudeIdentityCookieNames(cookieHeader)) {
    // Match Amplitude's documented Max-Age=0 removal, plus domain-scoped expire for hostname cookies.
    expireCookie(`${name}=; Max-Age=0; path=/; SameSite=Lax`);
    expireCookie(`${name}=; Max-Age=0; path=/; domain=${cookieDomain}; SameSite=Lax`);
  }
}

/**
 * Amplitude identity storage from in-memory consent preferences.
 *
 * Callers must pass preferences from CookieConsentManager (not re-read the cookie), so init
 * does not depend on Segment write timing or Segment APIs.
 *
 * When consent is required (EU / ?require-consent-manager): only EXPLICIT_AGREE → cookie;
 * otherwise none (no cookies/localStorage). Init may still run.
 *
 * When consent is not required: Missing Amplitude key → cookie; EXPLICIT_DISAGREE → none;
 * agree / implicit agree → cookie.
 */
export function getAmplitudeIdentityStorageFromPreferences(
  preferences: ConsentPreferences,
  isConsentRequired: boolean
): AmplitudeIdentityInitOptions {
  const amplitudeConsent = preferences[AMPLITUDE_DESTINATION_ID];
  let identityStorage: AmplitudeIdentityStorage;
  if (isConsentRequired) {
    identityStorage = amplitudeConsent === EXPLICIT_AGREE ? 'cookie' : 'none';
  } else if (amplitudeConsent === undefined) {
    identityStorage = 'cookie';
  } else {
    identityStorage = amplitudeConsent === EXPLICIT_DISAGREE ? 'none' : 'cookie';
  }

  if (identityStorage === 'cookie') {
    // Hostname-scoped so device identity stays per tenant instance (same as tracking-preferences).
    return {identityStorage, cookieOptions: {domain: window.location.hostname}};
  }
  return {identityStorage};
}

export function shouldInitializeAmplitude(apiKey: string | undefined): boolean {
  return Boolean(apiKey) && BenchlingConfig.ANALYTICS_ENABLE_AMPLITUDE && isSessionAnalyticsAllowed();
}

/**
 * Anonymize Amplitude identity (clear userId, rotate deviceId). No-ops when Amplitude
 * would not initialize. Does not delete AMP_* cookies — see clearAmplitudeIdentityCookies.
 */
export function resetAmplitude(): void {
  if (!shouldInitializeAmplitude(window.AMPLITUDE_API_KEY)) {
    return;
  }
  amplitude.reset();
}

export function initializeAmplitude(
  apiKey: string | undefined,
  preferences: ConsentPreferences,
  isConsentRequired: boolean
): void {
  if (!shouldInitializeAmplitude(apiKey)) {
    return;
  }

  const identityInit = getAmplitudeIdentityStorageFromPreferences(preferences, isConsentRequired);
  const privacyReady = createAmplitudePrivacyReadyLatch();

  // Hold events until the enrichment sanitizer is installed so the first page view is not raw.
  amplitude.add(createAmplitudePrivacyBeforePlugin(privacyReady.waitUntilReady));
  amplitude
    .init(apiKey!, {
      ...identityInit,
      logLevel: window.debug ? amplitude.Types.LogLevel.Verbose : amplitude.Types.LogLevel.Warn,
      transport: 'xhr',
      autocapture: {
        pageViews: {
          trackHistoryChanges: 'pathOnly',
        },
        sessions: true,
        pageUrlEnrichment: true,
        attribution: false,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: getAmplitudeElementInteractionsOptions(),
        frustrationInteractions: false,
        networkTracking: false,
        webVitals: false,
        performanceTracking: false,
      },
      trackingOptions: {
        ipAddress: false,
      },
    })
    .promise.then(() => amplitude.add(createAmplitudePrivacyEnrichmentPlugin()).promise)
    .then(() => {
      privacyReady.markReady();
    })
    .catch(() => {
      // Release the hold even on init failure so events are not stuck forever. The events will not be sent.
      privacyReady.markReady();
    });

  if (window.bootstrapUser) {
    const userId = `${BenchlingConfig.TENANT_ID}:${window.bootstrapUser.id}`;
    const identify = new amplitude.Identify();
    for (const [key, value] of Object.entries(window.bootstrapUser.publicIdentifyTraits ?? {})) {
      identify.set(key, value);
    }
    identify.set('enabledCapabilityFeatures', window.bootstrap?.enabledCapabilityFeatures ?? []);
    amplitude.identify(identify, {user_id: userId});
  }
  amplitude.setGroup('tenant', BenchlingConfig.TENANT_ID);
}
